import { useState, useEffect, useCallback } from 'react';

const DOC_TYPES = [
  { id: 'PO', label: 'Purchase Order' },
  { id: 'SO', label: 'Sales Order' },
  { id: 'TAX_INVOICE', label: 'Tax Invoice' },
  { id: 'TDS', label: 'TDS' },
  { id: 'OTHER', label: 'Other' },
];

const fmtSize = (b) => {
  if (!b) return '';
  if (b < 1024) return b + ' B';
  if (b < 1048576) return Math.round(b / 1024) + ' KB';
  return (b / 1048576).toFixed(1) + ' MB';
};

const fmtDate = (d) => {
  if (!d) return '';
  try {
    return new Date(d).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' });
  } catch {
    return d;
  }
};

const CustomerDocumentsTab = ({ accountId }) => {
  const [files, setFiles] = useState([]);
  const [loading, setLoading] = useState(true);
  const [uploading, setUploading] = useState(false);
  const [error, setError] = useState('');
  const [docType, setDocType] = useState('PO');
  const [refNo, setRefNo] = useState('');
  const [filter, setFilter] = useState('ALL');
  const [search, setSearch] = useState('');
  const [groupByRef, setGroupByRef] = useState(true);

  const authHeaders = () => ({
    Authorization: `Bearer ${localStorage.getItem('access_token')}`,
  });

  const openCombined = async (ref) => {
    try {
      const res = await fetch(
        `/api/v1/order-documents/combined?entity_id=${accountId}&reference_no=${encodeURIComponent(ref)}`,
        { headers: authHeaders() }
      );
      if (!res.ok) {
        alert('Could not open combined PDF (server said ' + res.status + '). Try logging in again.');
        return;
      }
      const blobUrl = URL.createObjectURL(await res.blob());
      window.open(blobUrl, '_blank');
    } catch (e) {
      alert('Could not open combined PDF: ' + e.message);
    }
  };

  const loadFiles = useCallback(async () => {
    if (!accountId) return;
    setLoading(true);
    try {
      const res = await fetch(
        `/api/v1/attachments?entity_type=account&entity_id=${accountId}`,
        { headers: authHeaders() },
      );
      if (!res.ok) throw new Error('Could not load documents');
      setFiles(await res.json());
      setError('');
    } catch (e) {
      setError(e.message);
    } finally {
      setLoading(false);
    }
  }, [accountId]);

  useEffect(() => {
    loadFiles();
  }, [loadFiles]);

  const handleUpload = async (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    if (['PO', 'SO', 'TAX_INVOICE'].includes(docType) && !refNo.trim()) {
      setError('Reference no is required for Purchase Order, Sales Order and Tax Invoice uploads. Use the order/PO number so documents of the same order stay together.');
      e.target.value = '';
      return;
    }

    setUploading(true);
    setError('');

    const form = new FormData();
    form.append('entity_type', 'account');
    form.append('entity_id', accountId);
    form.append('doc_type', docType);
    if (refNo) form.append('reference_no', refNo);
    form.append('file', file);

    try {
      const res = await fetch('/api/v1/attachments', {
        method: 'POST',
        headers: authHeaders(),
        body: form,
      });
      if (!res.ok) {
        const err = await res.json().catch(() => ({}));
        throw new Error(err.detail || 'Upload failed');
      }
      setRefNo('');
      await loadFiles();
    } catch (err) {
      setError(err.message);
    } finally {
      setUploading(false);
      e.target.value = '';
    }
  };

  const handleDownload = async (id, name) => {
    try {
      const res = await fetch(`/api/v1/attachments/${id}/download`, {
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Could not download file');
      const blob = await res.blob();
      const url = window.URL.createObjectURL(blob);
      window.open(url, '_blank');
    } catch (e) {
      setError(e.message);
    }
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Remove this document?')) return;
    try {
      const res = await fetch(`/api/v1/attachments/${id}`, {
        method: 'DELETE',
        headers: authHeaders(),
      });
      if (!res.ok) throw new Error('Delete failed');
      await loadFiles();
    } catch (e) {
      setError(e.message);
    }
  };

  const base = filter === 'ALL' ? files : files.filter((f) => f.doc_type === filter);
  const q = search.trim().toLowerCase();
  const shown = q
    ? base.filter(
        (f) =>
          (f.file_name || '').toLowerCase().includes(q) ||
          (f.reference_no || '').toLowerCase().includes(q),
      )
    : base;
  const groups = {};
  if (groupByRef) {
    shown.forEach((f) => {
      const k = f.reference_no || 'No reference';
      (groups[k] = groups[k] || []).push(f);
    });
  }

  return (
    <div className="space-y-4">
      <div className="border border-gray-200 rounded-lg p-4 bg-gray-50">
        <div className="flex flex-wrap items-end gap-3">
          <div>
            <label className="block text-xs text-gray-600 mb-1">Document type</label>
            <select
              value={docType}
              onChange={(e) => setDocType(e.target.value)}
              className="border border-gray-300 rounded px-3 py-1.5 text-sm"
            >
              {DOC_TYPES.map((t) => (
                <option key={t.id} value={t.id}>
                  {t.label}
                </option>
              ))}
            </select>
          </div>

          <div>
            <label className="block text-xs text-gray-600 mb-1">Reference no (optional)</label>
            <input
              type="text"
              value={refNo}
              onChange={(e) => setRefNo(e.target.value)}
              placeholder="e.g. PO-4471"
              className="border border-gray-300 rounded px-3 py-1.5 text-sm w-40"
            />
          </div>

          <label className={`px-4 py-2 rounded text-sm text-white cursor-pointer ${uploading ? 'bg-gray-400' : 'bg-blue-600 hover:bg-blue-700'}`}>
            {uploading ? 'Uploading...' : 'Choose file'}
            <input
              type="file"
              accept=".pdf,.jpg,.jpeg,.png"
              onChange={handleUpload}
              disabled={uploading}
              className="hidden"
            />
          </label>
        </div>
        <p className="text-xs text-gray-500 mt-2">PDF, JPG or PNG. Max 15 MB.</p>
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 text-sm px-3 py-2 rounded">
          {error}
        </div>
      )}

      <div className="flex items-center gap-4 flex-wrap">
        <input
          type="text"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          placeholder="Search file or reference no..."
          className="border border-gray-300 rounded px-3 py-1.5 text-sm w-64"
        />
        <label className="flex items-center gap-1.5 text-xs text-gray-600 cursor-pointer">
          <input
            type="checkbox"
            checked={groupByRef}
            onChange={(e) => setGroupByRef(e.target.checked)}
          />
          Group by order
        </label>
      </div>

      <div className="flex gap-2 flex-wrap">
        <button
          onClick={() => setFilter('ALL')}
          className={`px-3 py-1 text-xs rounded border ${filter === 'ALL' ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
        >
          All ({files.length})
        </button>
        {DOC_TYPES.map((t) => {
          const n = files.filter((f) => f.doc_type === t.id).length;
          return (
            <button
              key={t.id}
              onClick={() => setFilter(t.id)}
              className={`px-3 py-1 text-xs rounded border ${filter === t.id ? 'bg-blue-600 text-white border-blue-600' : 'bg-white text-gray-600 border-gray-300'}`}
            >
              {t.label} ({n})
            </button>
          );
        })}
      </div>

      {loading ? (
        <p className="text-sm text-gray-400">Loading...</p>
      ) : shown.length === 0 ? (
        <div className="text-center py-8 text-gray-400 text-sm border border-dashed border-gray-300 rounded-lg">
          No documents yet.
        </div>
      ) : (
        <div className="border border-gray-200 rounded-lg overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-gray-50 text-gray-600 text-xs">
              <tr>
                <th className="text-left px-4 py-2">File</th>
                <th className="text-left px-4 py-2">Type</th>
                <th className="text-left px-4 py-2">Ref</th>
                <th className="text-left px-4 py-2">Uploaded</th>
                <th className="text-right px-4 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {(groupByRef
                ? Object.entries(groups).flatMap(([ref, arr]) => [
                    { __header: true, id: 'hdr-' + ref, ref, count: arr.length },
                    ...arr,
                  ])
                : shown
              ).map((f) => f.__header ? (
                <tr key={f.id} className="bg-gray-100 border-t border-gray-200">
                  <td colSpan="4" className="px-4 py-1.5 text-xs font-semibold text-gray-700">
                    {f.ref} ({f.count} document{f.count > 1 ? 's' : ''})
                  </td>
                  <td className="px-4 py-1.5 text-right">
                    {f.ref && f.ref !== 'No reference' && (
                      <button
                        onClick={() => openCombined(f.ref)}
                        className="bg-blue-800 text-white text-xs px-2 py-1 rounded hover:bg-blue-900"
                      >
                        Merge & View
                      </button>
                    )}
                  </td>
                </tr>
              ) : (
                <tr key={f.id} className="border-t border-gray-100">
                  <td className="px-4 py-2">
                    {f.file_name}
                    <span className="text-xs text-gray-400 ml-2">{fmtSize(f.file_size)}</span>
                  </td>
                  <td className="px-4 py-2 text-xs">
                    <span className="bg-blue-50 text-blue-700 px-2 py-0.5 rounded">
                      {(DOC_TYPES.find((t) => t.id === f.doc_type) || {}).label || f.doc_type}
                    </span>
                  </td>
                  <td className="px-4 py-2 text-xs text-gray-600">{f.reference_no || '-'}</td>
                  <td className="px-4 py-2 text-xs text-gray-500">
                    {fmtDate(f.uploaded_at)} · {f.uploaded_by}
                  </td>
                  <td className="px-4 py-2 text-right whitespace-nowrap">
                    <button
                      onClick={() => handleDownload(f.id, f.file_name)}
                      className="text-blue-600 hover:underline text-xs mr-3"
                    >
                      View
                    </button>
                    <button
                      onClick={() => handleDelete(f.id)}
                      className="text-red-500 hover:underline text-xs"
                    >
                      Remove
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
};

export default CustomerDocumentsTab;
